import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH, VDJ_MIRROR_CHUNK } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';
import { Adapt } from './lib/adapt.js';
import { titleText } from './shapes.js';

// Contrat du rig partagé : interdit. Tout est en 2D plein écran.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'mixdeck',
  family: 'shader',
  danceability: 0.92,
  requiresTier: 0,
  energyLevel: 0.92,
  bpmMin: 118,
  bpmMax: 180,
  movement: 0.95,
  tonality: 0.35,
  enabled: true
};

// MIXDECK : un VJ qui MIXE. Trois couches (FOND, MILIEU, AVANT) prises dans
// une banque de petits générateurs, combinées par des modes de fusion qui
// changent, coupées net sur la mesure, « scratchées » sur les temps. LOOK
// VDJ party (lib/vdjLook.js). House, techno, EDM, big room, hardstyle.
//
// LA BANQUE. FOND (demi-résolution, quart au tier 0) : tunnel de polygones,
// ciel de plasma, zoom de damier, nébuleuse fbm, rayons tournants. MILIEU
// (pleine résolution) : pochette en mosaïque (grille 3 x 3 à 8 x 8, chaque
// case COUVRE sa cellule, R18), pochette en kaléidoscope, grille rétro-néon,
// barres de spectre géantes. AVANT (calculé DANS la passe finale, en pleine
// résolution et en distance en pixels, R1 : il ne passe par aucune texture) :
// formes qui frappent (cercle, triangle, croix), titre en très grandes
// capitales, stroboscope de lignes, anneaux depuis le centre. Chaque
// générateur reçoit le bus (énergie, bandes, phase de temps, impulsion) et
// la palette de la phrase. Sans pochette, mosaïque et kaléidoscope sont
// remplacés par la grille et les barres.
//
// LE MIXEUR. Milieu sur fond : ADD, SCREEN, MULTIPLY, DIFFERENCE. Avant sur
// le mix : ADD, SCREEN, DIFFERENCE, ou MASK (l'avant est un masque : le fond
// à travers les formes, le milieu ailleurs). Effets de sortie : séparation
// RGB, miroir (2 ou 4 quartiers, appliqué aux UV avant les couches : les
// traînées sont symétriques aussi), feedback zoom (FeedbackBuffer, l'image
// précédente agrandie : traînées vers l'extérieur, R15 : décroissance
// pow(0,5, dt / demi-vie d'un seizième de mesure), soustraction tramée de 1/255
// pour revenir au noir ; l'AVANT se dessine par-dessus, jamais dans le
// tampon), inversion, postérisation.
//
// CIBLES : fond (1/2), milieu (1) et la paire du feedback (tiers 1-2) ;
// tier 0 : fond (1/4) seulement, deux couches, sans feedback, une seule
// passe. Les générateurs non choisis ne tournent pas ; tous sont compilés
// à la première image (un changement de mesure ne compile rien).
//
// LE VJ (un automate) : sur chaque MESURE une seule chose change (un
// générateur d'une couche, un mode, un effet) ; sur la PHRASE et au début
// de chaque scène de 20 s (R20), un SET cohérent (palette + trio) avec une
// coupe nette sur le premier temps ; sur les TEMPS, le scratch (flash de
// l'avant, à-coup de zoom du feedback, inversion d'un huitième de temps sur
// les temps 2 et 4) ; en MONTÉE (drop annoncé sous 8 mesures) les couches
// se retirent (milieu, puis fond : l'avant seul sur noir en strobe de plus
// en plus rapide) ; au DROP tout revient d'un coup avec le set le plus
// intense, miroir à 4 quartiers et feedback ; mémoire des 6 derniers sets.
// adapt.intensity sous 0,6 : ni DIFFERENCE ni inversion, coupes sur la
// phrase seulement.
//
// Debug : ?mixset=<n> (set imposé, plus de changements), ?mixlayers=bg,mid,
// front (générateurs imposés, par nom), ?mixblend=add|screen|diff|mask,
// ?mixlog=1 (le journal du VJ), ?mixfb=0|1 (feedback imposé), ?mixprobe=1
// (window.__mixdeck).
//
// precision highp (exception documentée) : les distances en pixels de la
// couche avant et les UV du feedback ne tiennent pas en fp16 sur la TV.

const BG = ['tunnel', 'plasma', 'checker', 'nebula', 'rays'];
const MID = ['mosaic', 'kaleido', 'neon', 'bars'];
const FRONT = ['shapes', 'title', 'strobe', 'rings'];
const BLEND1 = ['add', 'screen', 'multiply', 'diff'];
const BLEND2 = ['add', 'screen', 'diff', 'mask'];
// coherent trios: [bg, mid, front]
const SETS = [
  ['tunnel', 'mosaic', 'shapes'], ['plasma', 'kaleido', 'rings'], ['checker', 'neon', 'title'], ['nebula', 'bars', 'strobe'],
  ['rays', 'kaleido', 'shapes'], ['tunnel', 'bars', 'rings'], ['checker', 'mosaic', 'title'], ['nebula', 'neon', 'shapes'],
  ['plasma', 'mosaic', 'strobe'], ['rays', 'bars', 'title'], ['tunnel', 'kaleido', 'rings'], ['plasma', 'neon', 'shapes']
];
const TIER = {
  0: { bgScale: 0.25, mid: false, feedback: false, fbScale: 0 },
  1: { bgScale: 0.5, mid: true, feedback: true, fbScale: 0.5 },
  2: { bgScale: 0.5, mid: true, feedback: true, fbScale: 1.0 }
};
const CYCLE = 20;
const MEMORY = 6;
const NO_BPM = 128;
const SAFE_FLOOR = 0.5;
const LUM_W = 192, LUM_H = 108;
const ACCENT_FALLBACK = [0xff2bd6, 0x2bffea, 0xffc21a];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function mulberry(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- shaders -----------------------------------------------------------------

const quadVertex = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// the bus every generator reads
const BUS = `
  precision highp float;
  uniform vec2 uRes;
  uniform float uAspect, uTime, uBeats, uBeatPhase, uBarPhase, uPulse, uBarPulse, uEnergy, uSeed, uBarIndex;
  uniform vec3 uBands, uCol0, uCol1, uCol2;
  uniform sampler2D uCover;
  uniform float uHasCover;
  varying vec2 vUv;
  float hash1(float n) { return fract(sin(n * 127.1 + 311.7) * 43758.5453); }
  float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash2(i), hash2(i + vec2(1.0, 0.0)), f.x), mix(hash2(i + vec2(0.0, 1.0)), hash2(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int k = 0; k < 4; k++) { v += a * vnoise(p); p = p * 2.03 + vec2(1.7, 9.2); a *= 0.5; }
    return v;
  }
  // height units, centred on the STAGE centre (R19: the screen's)
  vec2 stageP(vec2 uv) { return (uv - 0.5) * vec2(uAspect, 1.0); }
`;

const bgFragments = {
  tunnel: BUS + `
    void main() {
      vec2 p = stageP(vUv);
      float n = 3.0 + floor(mod(uSeed * 7.0, 4.0));
      float a = atan(p.y, p.x) + uTime * 0.12 * (mod(uSeed * 3.0, 2.0) < 1.0 ? 1.0 : -1.0);
      float r = length(p) + 1e-4;
      float k = 6.2831853 / n, am = mod(a + k * 0.5, k) - k * 0.5;
      float pr = r * cos(am);
      float z = 0.22 / pr + uBeats * 0.5;
      float stripe = smoothstep(0.35, 0.5, abs(fract(z) - 0.5));
      float edge = smoothstep(0.03, 0.0, abs(abs(am) - k * 0.5) * r * 3.0);
      float fade = smoothstep(0.02, 0.22, pr) * (1.0 - smoothstep(0.8, 1.6, pr));
      vec3 c = mix(uCol0, uCol1, stripe) * 0.30 * fade + uCol2 * edge * 0.6 * fade * (0.6 + 0.4 * uPulse);
      gl_FragColor = vec4(c, 1.0);
    }`,
  plasma: BUS + `
    void main() {
      vec2 p = stageP(vUv);
      float t = uTime * 0.6 + uBeats * 0.15;
      float v = sin(p.x * 3.0 + t) + sin(p.y * 3.3 - t * 0.7) + sin((p.x + p.y) * 2.2 + t * 1.3) + sin(length(p) * 5.0 - t * 2.0);
      v = v * 0.125 + 0.5;
      vec3 c = mix(uCol0, uCol1, smoothstep(0.25, 0.75, v)) * 0.45 + uCol2 * pow(0.5 + 0.5 * sin(v * 12.566), 4.0) * 0.35 * (0.5 + 0.5 * uEnergy);
      gl_FragColor = vec4(c, 1.0);
    }`,
  checker: BUS + `
    void main() {
      vec2 p = stageP(vUv);
      float r = length(p) + 1e-4, a = atan(p.y, p.x);
      float u = log(r) * 1.6 - uBeats * 0.25;
      float v = a / 6.2831853 * (6.0 + 2.0 * floor(mod(uSeed * 5.0, 3.0))) + uTime * 0.05;
      float c0 = sin(u * 6.2831853) * sin(v * 6.2831853);
      float ch = smoothstep(-0.25, 0.25, c0);
      vec3 c = mix(uCol0, uCol1, ch) * 0.30 * smoothstep(0.0, 0.18, r) * (1.0 - smoothstep(0.9, 1.5, r));
      gl_FragColor = vec4(c, 1.0);
    }`,
  nebula: BUS + `
    void main() {
      vec2 p = stageP(vUv);
      vec2 q = p * 1.4 + vec2(uTime * 0.04, uSeed);
      // the warp's amplitude stays under half the warped noise's wavelength (R14)
      vec2 w = vec2(fbm(q + vec2(1.7, 9.2)), fbm(q + vec2(8.3, 2.8))) - 0.5;
      float f = fbm(q + 0.45 * w);
      vec3 c = mix(uCol0 * 0.10, uCol1 * 0.55, smoothstep(0.32, 0.72, f)) + uCol2 * smoothstep(0.66, 0.92, f) * 0.45 * (0.6 + 0.4 * uEnergy);
      gl_FragColor = vec4(c, 1.0);
    }`,
  rays: BUS + `
    void main() {
      vec2 p = stageP(vUv);
      float a = atan(p.y, p.x), r = length(p);
      float n = 10.0 + 4.0 * floor(mod(uSeed * 11.0, 3.0));
      float ray = pow(0.5 + 0.5 * sin(a * n + uTime * 0.7), 3.0);
      float ray2 = pow(0.5 + 0.5 * sin(a * n * 0.5 - uTime * 0.45 + 1.0), 6.0);
      vec3 c = (uCol0 * ray * 0.45 + uCol1 * ray2 * 0.35) * smoothstep(1.3, 0.1, r) + uCol2 * 0.12 * exp(-r * 4.0) * (1.0 + uPulse);
      gl_FragColor = vec4(c, 1.0);
    }`
};

// the cover, covering a cell of aspect `asp` (R18: scale = max)
const COVER_CELL = `
  vec3 coverCell(vec2 cu, float asp) {
    vec2 s = asp >= 1.0 ? vec2(1.0, 1.0 / asp) : vec2(asp, 1.0);
    vec2 uv = (cu - 0.5) * s + 0.5;
    vec3 t = texture2D(uCover, uv).rgb;
    return pow(t, vec3(1.4)) * 0.55;
  }
`;
const midFragments = {
  mosaic: BUS + COVER_CELL + `
    void main() {
      float cols = 3.0 + floor(mod(uSeed * 13.0, 6.0));
      float rows = max(2.0, floor(cols / uAspect + 0.5));
      vec2 g = vUv * vec2(cols, rows);
      float row = floor(g.y);
      // rows scroll on the beats, alternately
      g.x += (mod(row, 2.0) < 1.0 ? 1.0 : -1.0) * floor(uBeats) * 0.5 + 0.5 * smoothstep(0.0, 0.25, uBeatPhase) * (mod(row, 2.0) < 1.0 ? 1.0 : -1.0);
      vec2 cell = floor(g), cu = fract(g);
      float asp = uAspect * rows / cols;
      vec3 c = uHasCover > 0.5 ? coverCell(cu, asp) : mix(uCol0, uCol1, hash2(cell)) * 0.5;
      // a checker of cells lights with the beat
      float chk = mod(cell.x + cell.y, 2.0);
      c *= 0.55 + 0.45 * mix(chk, 1.0 - chk, step(0.5, fract(uBeats * 0.5))) * (0.6 + 0.4 * uPulse);
      // the grout
      vec2 e = abs(cu - 0.5);
      float grout = smoothstep(0.47, 0.49, max(e.x, e.y));
      c = mix(c, uCol2 * 0.5, grout);
      gl_FragColor = vec4(c, 1.0);
    }`,
  kaleido: BUS + VDJ_MIRROR_CHUNK + `
    void main() {
      vec2 m = vdjMirror(vUv, mod(uSeed * 17.0, 2.0) < 1.0 ? 3.0 : 4.0, uAspect);
      float z = 0.55 + 0.15 * sin(uBeats * 0.39) - 0.08 * uPulse;
      vec2 cu = (m - 0.5) * z + 0.5 + vec2(uTime * 0.02, uTime * 0.013);
      cu = 1.0 - abs(1.0 - mod(cu, 2.0));
      vec3 c = uHasCover > 0.5 ? pow(texture2D(uCover, cu).rgb, vec3(1.4)) * 0.55 : mix(uCol0, uCol1, vnoise(cu * 4.0)) * 0.5;
      gl_FragColor = vec4(c, 1.0);
    }`,
  neon: BUS + `
    void main() {
      vec2 p = stageP(vUv);
      float y = p.y;
      float depth = 0.12 / max(abs(y), 0.004);
      float x = p.x * depth;
      float zz = depth + uBeats * 0.5;
      // R1: line widths from the screen-space derivatives of the grid coordinates
      float fx = fract(x * 1.5), fz = fract(zz);
      float dx = min(fx, 1.0 - fx) / max(fwidth(x * 1.5), 1e-5);
      float dz = min(fz, 1.0 - fz) / max(fwidth(zz), 1e-5);
      float d = min(dx, dz);
      float core = 1.0 - smoothstep(0.8, 1.6, d);
      float glow = exp(-d / 6.0) * 0.4;
      float fade = 1.0 - smoothstep(2.0, 14.0, depth);
      vec3 c = (vec3(1.0) * core * 0.9 + uCol0 * (0.7 * (1.0 - smoothstep(1.6, 3.0, d)) + glow)) * fade * (0.8 + 0.2 * uPulse);
      c += uCol1 * exp(-abs(y) * 10.0) * 0.35;
      gl_FragColor = vec4(c, 1.0);
    }`,
  bars: BUS + `
    void main() {
      float n = 16.0;
      float i = floor(vUv.x * n);
      float t = (i + 0.5) / n;
      float band = t < 0.5 ? mix(uBands.x, uBands.y, t * 2.0) : mix(uBands.y, uBands.z, t * 2.0 - 1.0);
      float h = (0.12 + 0.75 * band) * (0.6 + 0.4 * hash2(vec2(i, floor(uBeats * 2.0)))) * (0.75 + 0.25 * uPulse);
      float yy = abs(vUv.y - 0.5) * 2.0;                 // mirrored top/bottom
      float px = 1.0 / uRes.y;
      float inside = 1.0 - smoothstep(h - px, h + px, yy);
      float gx = fract(vUv.x * n);
      float gap = smoothstep(0.08, 0.12, gx) * (1.0 - smoothstep(0.88, 0.92, gx));
      float top = 1.0 - smoothstep(0.0, 2.5 * px, abs(yy - h));
      vec3 c = mix(uCol0, uCol1, yy / max(h, 1e-3)) * 0.55 * inside * gap + uCol2 * top * gap * 0.9 + vec3(1.0) * top * gap * (1.0 - smoothstep(0.0, 1.2 * px, abs(yy - h))) * 0.8;
      gl_FragColor = vec4(c, 1.0);
    }`
};

// the mixer: mode 0 = content into the feedback target, 1 = display from
// the feedback target, 2 = direct display (no feedback)
const mixFragment = BUS + VDJ_MIRROR_CHUNK + `
  uniform sampler2D uBg, uMid, uPrev, uTitle;
  uniform float uMode, uHasMid, uBgOn, uBlend1, uBlend2, uFront, uMirror, uZoom, uDecay, uDither, uInvert, uPoster, uSplit, uFlash, uGain, uFrontGain, uStrobeRate, uTitleAspect, uMeasureK;
  uniform vec4 uRings;           // launch beats of the last 4 rings
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  float lum(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
  float safeAtt(vec2 uv) {
    vec2 s = (uv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  vec3 blendMode(vec3 a, vec3 b, float m) {
    if (m < 0.5) return a + b;
    if (m < 1.5) return 1.0 - (1.0 - a) * (1.0 - b);
    if (m < 2.5) return a * b * 2.0;
    return abs(a - b);
  }
  // signed distances, height units
  float sdCircle(vec2 p, float r) { return length(p) - r; }
  float sdTri(vec2 p, float r) {
    const float k = 1.7320508;
    p.x = abs(p.x) - r; p.y = p.y + r / k;
    if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
    p.x -= clamp(p.x, -2.0 * r, 0.0);
    return -length(p) * sign(p.y);
  }
  float sdCross(vec2 p, float r) {
    p = abs(p);
    vec2 d1 = abs(p - vec2(0.0, 0.0)) - vec2(r, r * 0.28);
    vec2 d2 = abs(p) - vec2(r * 0.28, r);
    float a = length(max(d1, 0.0)) + min(max(d1.x, d1.y), 0.0);
    float b = length(max(d2, 0.0)) + min(max(d2.x, d2.y), 0.0);
    return min(a, b);
  }
  // R1: a neon outline from a pixel distance
  vec3 neon(float dPx, vec3 col, float I) {
    float core = 1.0 - smoothstep(1.0, 2.0, dPx);
    float sheath = 1.0 - smoothstep(2.0, 6.0, dPx);
    float halo = exp(-dPx / 10.0) * 0.35;
    return (vec3(1.0) * core * smoothstep(0.4, 0.8, I) + col * (0.7 * sheath + halo)) * min(I, 1.0);
  }
  // the FRONT layer: colour and coverage (the MASK)
  vec4 front(vec2 uv, vec2 p, float px) {
    float k = uMeasureK;
    if (uFront < 0.5) {
      // shapes that hit: big on the beat, shrinking after
      float r = 0.26 + 0.12 * uPulse;
      float which = mod(uBarIndex, 3.0);
      float sd = which < 0.5 ? sdCircle(p, r) : which < 1.5 ? sdTri(p * vec2(1.0, -1.0), r * 1.15) : sdCross(p, r * 1.1);
      float cov = 1.0 - smoothstep(-px, px, sd);
      vec3 c = neon(abs(sd) / px, uCol2, 0.9 + 0.6 * uPulse) * k;
      return vec4(c, cov);
    }
    if (uFront < 1.5) {
      // the title: a band 22 % of the height at the stage centre, punched on the bar
      float hh = 0.11 * (1.0 + 0.22 * uBarPulse);
      float hw = min(hh * uTitleAspect, 0.46 * uAspect);
      vec2 q = p / vec2(hw, hh);
      if (abs(q.x) > 1.0 || abs(q.y) > 1.0) return vec4(0.0);
      float a = texture2D(uTitle, q * 0.5 + 0.5).a;
      float cov = smoothstep(0.35, 0.65, a);
      vec3 c = mix(uCol2, vec3(1.0), 0.6) * cov * (0.75 + 0.25 * uPulse);
      return vec4(c, cov);
    }
    if (uFront < 2.5) {
      // the line strobe: on for 35 % of its period
      float on = step(fract(uBeats * uStrobeRate), 0.35);
      float horiz = mod(uBarIndex, 2.0) < 1.0 ? 1.0 : 0.0;
      float n = 4.0 + 2.0 * floor(mod(uSeed * 19.0, 3.0));
      float v = horiz > 0.5 ? uv.y : uv.x;
      float cells = horiz > 0.5 ? uRes.y / n : uRes.x / n;
      float f = fract(v * n + 0.5) - 0.5;
      float dPx = abs(f) * cells;
      vec3 c = neon(dPx, uCol0, 0.9) * 0.6 * on * k;
      float cov = (1.0 - smoothstep(1.0, 3.0, dPx)) * on;
      return vec4(c, cov);
    }
    // rings from the centre, one per beat, four alive
    vec3 c = vec3(0.0); float cov = 0.0;
    float r = length(p);
    for (int i = 0; i < 4; i++) {
      float b0 = i == 0 ? uRings.x : i == 1 ? uRings.y : i == 2 ? uRings.z : uRings.w;
      float age = uBeats - b0;
      if (age < 0.0 || age > 3.5) continue;
      float R = age * 0.42;
      float dPx = abs(r - R) / px;
      float I = 1.2 * (1.0 - age / 3.5);
      c += neon(dPx, mix(uCol0, uCol1, fract(b0 * 0.37)), I) * k;
      cov = max(cov, (1.0 - smoothstep(0.0, 1.0, abs(r - R) / 0.03)));
    }
    return vec4(min(c, 1.5), cov);
  }
  // the layers' mix stays under the bloom threshold (0.78): a bright cover
  // in SCREEN plus the shared bloom whited the frame out
  vec3 knee(vec3 c) {
    c *= 0.8;
    float L = lum(c);
    if (L > 0.4) c *= (0.4 + 0.10 * (1.0 - exp(-(L - 0.4) / 0.10))) / L;     // ceiling 0.50: the shared bloom lifted a 0.7 frame by half, and full-frame layers at 0.55 read pastel
    return c;
  }
  vec3 content(vec2 uv, vec2 p, float px) {
    vec3 bg = uBgOn > 0.5 ? texture2D(uBg, uv).rgb * 0.8 : vec3(0.0);
    if (uHasMid < 0.5) return knee(bg);
    vec3 mid = texture2D(uMid, uv).rgb;
    vec3 c;
    if (uBlend2 > 3.5) {                         // MASK: the background through the shapes, the middle elsewhere
      float cov = front(uv, p, px).a;
      c = mix(mid, bg, cov);
    } else c = blendMode(bg, mid, uBlend1);
    return knee(c);
  }
  void main() {
    vec2 uv0 = vUv;
    vec2 uv = vdjMirror(uv0, uMirror, uAspect);
    vec2 p = stageP(uv);
    float px = 1.0 / uRes.y;
    vec3 c;
    if (uMode < 0.5) {
      // content into the feedback target, with the previous frame's trail zoomed outward (R15)
      c = content(uv, p, px);
      vec2 zu = (uv0 - 0.5) / uZoom + 0.5;
      vec3 prev = texture2D(uPrev, zu).rgb * uDecay;
      prev = max(prev - (1.0 / 255.0) * step(hash2(gl_FragCoord.xy + uTime), uDither), 0.0);
      c = max(c, prev);                       // the trail is a ghost of what was there, never brighter than the content (a screen-sum saturated static layers to white)
      gl_FragColor = vec4(c, 1.0);
      return;
    }
    if (uMode < 1.5) {
      // display from the feedback target, the RGB split in the read
      vec2 o = vec2(uSplit * px, 0.0);
      c = vec3(texture2D(uPrev, uv0 + o).r, texture2D(uPrev, uv0).g, texture2D(uPrev, uv0 - o).b);
    } else {
      c = content(uv, p, px);
    }
    if (uBlend2 < 3.5 || uHasMid < 0.5) {         // (MASK without a middle layer would hide the front: it adds)
      vec4 f = front(uv, p, px);
      c = blendMode(c, f.rgb * uFrontGain, uBlend2 > 3.5 ? 0.0 : uBlend2);
    }
    if (uInvert > 0.5) c = (1.0 - c) * 0.75;        // the eighth-beat inversion: a bright hit, not a white wall
    if (uPoster > 0.5) c = floor(c * 5.0 + 0.5) / 5.0;
    c = mix(c, vec3(1.0), uFlash);
    c *= safeAtt(uv0) * uGain;
    c += (hash2(gl_FragCoord.xy) - 0.5) / 255.0;
    gl_FragColor = vec4(max(c, 0.0), 1.0);
  }
`;

// --- the title texture ---------------------------------------------------------

function makeTitleTexture(text) {
  if (typeof document === 'undefined' || !text) return null;
  const FONT = 160;
  const cv = document.createElement('canvas');
  const ctx = cv.getContext('2d');
  const font = `900 ${FONT}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  ctx.font = font;
  const w = Math.min(4096, Math.ceil(ctx.measureText(text).width) + 40), h = Math.ceil(FONT * 1.25);
  cv.width = w; cv.height = h;
  ctx.font = font; ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
  ctx.fillText(text, 20, h / 2);
  const t = new THREE.CanvasTexture(cv);
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter; t.generateMipmaps = false;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  return { texture: t, aspect: w / h };
}

// The generators, for hosts that show them elsewhere (MainStage's LED screens).
export { BUS as MIXDECK_BUS, bgFragments as MIXDECK_BG, quadVertex as MIXDECK_QUAD };

export class MixdeckVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._layout = TIER[this._tier];
    const fs = parseInt(urlParam('mixset'), 10);
    this._forcedSet = Number.isFinite(fs) ? ((fs % SETS.length) + SETS.length) % SETS.length : null;
    const fl = urlParam('mixlayers');
    this._forcedLayers = fl ? fl.split(',') : null;
    const fb = urlParam('mixblend');
    this._forcedBlend = fb && (BLEND1.includes(fb) || BLEND2.includes(fb)) ? fb : null;
    this._log = urlParam('mixlog') === '1';
    const ffb = urlParam('mixfb');
    this._forcedFb = ffb === '1' ? true : ffb === '0' ? false : null;
    this._post = new VdjPost({ bloomStrength: 0.10, bloomThreshold: 0.78, grain: 0.015, vignette: 0.3 });   // 0.35 veiled the frame: full-screen layers feed the bloom everywhere
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    this._clock = new SceneClock({ period: CYCLE, start: 0 });
    this._adapt = new Adapt();
    this._rng = mulberry(0x51ED270F ^ Math.floor((this._clock.elapsed + 1) * 977));
    this._decisions = [];
    if (urlParam('mixprobe') === '1' && typeof window !== 'undefined') window.__mixdeck = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    this._time = 0; this._beats = 0; this._beatCount = 0; this._barCount = 0; this._phraseCount = 0;
    this._beatPeriod = 60 / NO_BPM; this._free = 0; this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrasePhase = null;
    this._lastBeatAt = -10; this._lastBarAt = -10; this._build = 0; this._energy = 0.6; this._bands = new THREE.Vector3(0.4, 0.4, 0.4);
    this._rings = [-10, -10, -10, -10]; this._ringIdx = 0; this._lastDropKey = null; this._dropAt = -10;
    this._invertUntil = -1; this._flash = 0; this._zoomKick = 0; this._frontFlash = 0; this._pendingSet = false; this._sinceRender = 1 / 60;
    this._memory = []; this._coverUrl = undefined; this._cover = null; this._coverPending = null; this._titleKey = undefined; this._title = null;
    this._lumAge = 0; this._lumPending = false; this._lumFresh = false; this._lumMeasured = 0.2; this._lumSince = 0; this._lumPixels = new Uint8Array(LUM_W * LUM_H * 4);
    this._compiled = false; this._size = new THREE.Vector2();

    const safe = { uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    // the bus, shared by every material (same uniform objects)
    this._bus = {
      uRes: { value: new THREE.Vector2(1280, 720) }, uAspect: { value: 16 / 9 }, uTime: { value: 0 }, uBeats: { value: 0 }, uBeatPhase: { value: 0 }, uBarPhase: { value: 0 },
      uPulse: { value: 0 }, uBarPulse: { value: 0 }, uEnergy: { value: 0.6 }, uSeed: { value: 0.37 }, uBarIndex: { value: 0 },
      uBands: { value: this._bands }, uCol0: { value: new THREE.Color(1, 0.2, 0.8) }, uCol1: { value: new THREE.Color(0.2, 1, 0.9) }, uCol2: { value: new THREE.Color(1, 0.8, 0.2) },
      uCover: { value: null }, uHasCover: { value: 0 }
    };
    const mat = (fs, extra) => new THREE.ShaderMaterial({ uniforms: Object.assign({}, this._bus, extra || {}), vertexShader: quadVertex, fragmentShader: fs, depthTest: false, depthWrite: false });
    this._bgMats = {}; for (const k of BG) this._bgMats[k] = mat(bgFragments[k]);
    this._midMats = {}; for (const k of MID) this._midMats[k] = mat(midFragments[k]);
    this._mixU = {
      uBg: { value: null }, uMid: { value: null }, uPrev: { value: null }, uTitle: { value: null },
      uMode: { value: 2 }, uHasMid: { value: 0 }, uBgOn: { value: 1 }, uBlend1: { value: 0 }, uBlend2: { value: 0 }, uFront: { value: 0 }, uMirror: { value: 0 },
      uZoom: { value: 1.02 }, uDecay: { value: 0.9 }, uDither: { value: 0.5 }, uInvert: { value: 0 }, uPoster: { value: 0 }, uSplit: { value: 0 }, uFlash: { value: 0 }, uGain: { value: 1 },
      uFrontGain: { value: 1 }, uStrobeRate: { value: 2 }, uTitleAspect: { value: 6 }, uMeasureK: { value: 1 }, uRings: { value: new THREE.Vector4(-10, -10, -10, -10) }
    };
    this._mixMat = mat(mixFragment, Object.assign(this._mixU, safe));
    const quad = () => { const q = new THREE.Mesh(new THREE.PlaneGeometry(1, 1)); q.frustumCulled = false; return q; };
    this._passScene = new THREE.Scene(); this._passQuad = quad(); this._passScene.add(this._passQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    const rt = () => new THREE.WebGLRenderTarget(4, 4, { type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    this._rtBg = rt(); this._rtMid = this._layout.mid ? rt() : null; this._rtTmp = rt();
    this._feedback = this._layout.feedback ? new FeedbackBuffer({ scale: this._layout.fbScale }) : null;
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_W, LUM_H, { depthBuffer: false, stencilBuffer: false });
    // the display quad in the shared scene
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mixMat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);
    // the first set, cut
    this._state = { set: -1, bg: 'tunnel', mid: 'mosaic', front: 'shapes', blend1: 'add', blend2: 'add', mirror: 0, feedback: this._layout.feedback, poster: false, pal: 0 };
    this._newSet('scene');
  }

  // --- the VJ ----------------------------------------------------------------------

  _say(what) {
    this._decisions.push({ t: +this._time.toFixed(2), bar: this._barCount, what });
    if (this._decisions.length > 64) this._decisions.shift();
    if (this._log) console.log('[mixdeck] ' + this._time.toFixed(2) + 's bar ' + this._barCount + ': ' + what);
  }
  _key(s) { return [s.bg, s.mid, s.front, s.blend1, s.blend2, s.mirror, s.feedback ? 1 : 0, s.pal].join('/'); }
  _pick(arr, not) { let v = arr[Math.floor(this._rng() * arr.length)]; for (let k = 0; k < 8 && v === not; k++) v = arr[Math.floor(this._rng() * arr.length)]; return v; }
  _intense() { return this._adapt.get().intensity >= 0.6; }

  // a coherent set, not among the last six; `why` for the log
  _newSet(why) {
    const s = this._state, rng = this._rng;
    let idx = this._forcedSet;
    let trio = null, tries = 0;
    do {
      idx = this._forcedSet != null ? this._forcedSet : Math.floor(rng() * SETS.length);
      trio = SETS[idx];
      s.set = idx; s.bg = trio[0]; s.mid = trio[1]; s.front = trio[2];
      s.pal = Math.floor(rng() * 3);
      s.blend1 = this._intense() ? this._pick(BLEND1) : this._pick(['add', 'screen', 'multiply']);
      s.blend2 = this._pick(BLEND2);
      s.mirror = rng() < 0.35 ? (rng() < 0.5 ? 1 : 2) : 0;
      s.feedback = this._layout.feedback && rng() < 0.55;
      s.poster = rng() < 0.2;
      tries++;
    } while (this._forcedSet == null && tries < 20 && this._memory.includes(this._key(s)));
    if (this._forcedLayers) { if (BG.includes(this._forcedLayers[0])) s.bg = this._forcedLayers[0]; if (MID.includes(this._forcedLayers[1])) s.mid = this._forcedLayers[1]; if (FRONT.includes(this._forcedLayers[2])) s.front = this._forcedLayers[2]; }
    if (this._forcedBlend) { if (BLEND1.includes(this._forcedBlend)) s.blend1 = this._forcedBlend; if (BLEND2.includes(this._forcedBlend)) s.blend2 = this._forcedBlend; }
    this._memory.push(this._key(s)); while (this._memory.length > MEMORY) this._memory.shift();
    this._bus.uSeed.value = rng();
    this._applyPalette(this._lastProfile, true);
    this._say('SET (' + why + ') ' + this._key(s));
  }

  // one thing changes on the bar
  _barChange() {
    if (this._forcedSet != null || !this._intense()) return;
    const s = this._state, r = this._rng();
    if (r < 0.2) { s.bg = this._pick(BG, s.bg); this._say('bg -> ' + s.bg); }
    else if (r < 0.38 && this._layout.mid) { s.mid = this._pick(MID, s.mid); this._say('mid -> ' + s.mid); }
    else if (r < 0.56) { s.front = this._pick(FRONT, s.front); this._say('front -> ' + s.front); }
    else if (r < 0.68) { s.blend1 = this._pick(BLEND1, s.blend1); this._say('blend1 -> ' + s.blend1); }
    else if (r < 0.80) { s.blend2 = this._pick(BLEND2, s.blend2); this._say('blend2 -> ' + s.blend2); }
    else if (r < 0.88) { s.mirror = (s.mirror + 1) % 3; this._say('mirror -> ' + s.mirror); }
    else if (r < 0.95 && this._layout.feedback) { s.feedback = !s.feedback; this._say('feedback -> ' + s.feedback); }
    else { s.poster = !s.poster; this._say('poster -> ' + s.poster); }
    if (this._forcedBlend) { if (BLEND1.includes(this._forcedBlend)) s.blend1 = this._forcedBlend; if (BLEND2.includes(this._forcedBlend)) s.blend2 = this._forcedBlend; }
  }

  _dropSet() {
    const s = this._state;
    s.blend1 = 'diff'; s.blend2 = this._pick(['add', 'screen']); s.mirror = 2; s.feedback = this._layout.feedback; s.poster = false;
    s.bg = this._pick(['tunnel', 'rays', 'checker']); s.mid = this._pick(this._cover ? ['kaleido', 'mosaic'] : ['neon', 'bars']); s.front = this._pick(['shapes', 'rings']);
    this._bus.uSeed.value = this._rng();
    this._memory.push(this._key(s)); while (this._memory.length > MEMORY) this._memory.shift();
    this._say('DROP ' + this._key(s));
  }

  _applyPalette(profile, force) {
    const pal = profile && profile.artworkPalette;
    const src = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : ACCENT_FALLBACK;
    const key = src.join('|') + '#' + this._state.pal;
    if (!force && key === this._palKey) return;
    this._palKey = key;
    const cols = src.map((h) => vdjColor(h));
    const n = cols.length, o = this._state.pal;
    this._bus.uCol0.value.copy(cols[o % n]);
    this._bus.uCol1.value.copy(cols[(o + 1) % n]);
    this._bus.uCol2.value.copy(cols[(o + 2) % n]);
    // two colours too close in hue read as one (R4): push the second apart
    const h0 = {}, h1 = {};
    this._bus.uCol0.value.getHSL(h0); this._bus.uCol1.value.getHSL(h1);
    const dh = Math.min(Math.abs(h0.h - h1.h), 1 - Math.abs(h0.h - h1.h));
    if (dh < 0.25) this._bus.uCol1.value.offsetHSL(0.33, 0, 0);
  }

  _loadCover(url) {
    this._coverUrl = url;
    if (!url) { this._dropCover(); return; }
    const token = {};
    this._coverPending = token;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (texture) => {
      if (this._coverPending !== token) { texture.dispose(); return; }
      this._coverPending = null;
      if (this._cover) this._cover.dispose();
      this._cover = texture;
      this._bus.uCover.value = texture; this._bus.uHasCover.value = 1;
    }, () => { if (this._coverPending === token) { this._coverPending = null; this._dropCover(); } });
  }
  _dropCover() {
    if (this._cover) this._cover.dispose();
    this._cover = null; this._bus.uCover.value = null; this._bus.uHasCover.value = 0;
  }
  _prepareTitle(profile) {
    const text = titleText(profile && profile.title);
    if (text === this._titleKey) return;
    this._titleKey = text;
    if (this._title) { this._title.texture.dispose(); this._title = null; }
    this._title = makeTitleTexture(text);
    this._mixU.uTitle.value = this._title ? this._title.texture : null;
    this._mixU.uTitleAspect.value = this._title ? this._title.aspect : 6;
  }

  // --- per frame ------------------------------------------------------------------

  update(deltaTime, profile) {
    const paused = profile.playbackState !== undefined && profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    this._lastProfile = profile;
    const a = this._adapt.update(dt, profile);
    this._clock.update(dt);
    if ((profile.artworkUrl || null) !== this._coverUrl) this._loadCover(profile.artworkUrl || null);
    this._prepareTitle(profile);
    this._applyPalette(profile, false);
    if (paused) return;                                           // the mix holds
    this._time += dt;
    const director = profile.director || {};
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    this._beatPeriod = 60 / THREE.MathUtils.clamp(bpm, 60, 200);
    const bands = director.hasReliableGrid !== false && director.bands ? director.bands : null;
    const tb = bands ? [clamp01(bands.low || 0), clamp01(bands.mid || 0), clamp01(bands.high || 0)] : [0.5, 0.4, 0.4];
    this._bands.x += (tb[0] - this._bands.x) * Math.min(1, dt / 0.06);
    this._bands.y += (tb[1] - this._bands.y) * Math.min(1, dt / 0.06);
    this._bands.z += (tb[2] - this._bands.z) * Math.min(1, dt / 0.05);
    const energy = director.intensity != null ? clamp01(director.intensity) : 0.6;
    this._energy += (energy - this._energy) * Math.min(1, dt / 1.0);

    // the beat clock
    const beatPhase = director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) { if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true; this._lastBeatPhase = beatPhase; }
    else { this._free += dt; if (this._free >= this._beatPeriod) { this._free -= this._beatPeriod; beatEdge = true; } }
    this._beats += dt / this._beatPeriod;
    const bp = beatPhase != null ? beatPhase : this._free / this._beatPeriod;
    const barPhase = director.bar != null ? director.bar : profile.barPhase;
    let barEdge = false;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) barEdge = true; this._lastBarPhase = barPhase; }
    else if (beatEdge && this._beatCount % 4 === 0 && this._beatCount > 0) barEdge = true;
    const phrasePhase = director.phrase != null ? director.phrase : profile.phrasePhase;
    let phraseEdge = false;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) phraseEdge = true; this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % 32 === 0 && this._beatCount > 0) phraseEdge = true;
    if (beatEdge) {
      this._beatCount++; this._lastBeatAt = this._time;
      this._beats = Math.round(this._beats);                       // the continuous count lands exactly on the beat
      this._rings[this._ringIdx % 4] = this._beats; this._ringIdx++;
      // the scratch: a flash of the front, a zoom kick, an inversion on 2 and 4
      this._frontFlash = 1; this._zoomKick = 1;
      const inBar = ((this._beatCount - 1) % 4);
      if ((inBar === 1 || inBar === 3) && this._intense() && this._build < 0.3 && this._comfort.requestFlash(this._time)) this._invertUntil = this._time + this._beatPeriod / 8;
    }
    if (barEdge) { this._barCount++; this._lastBarAt = this._time; }
    if (phraseEdge) this._phraseCount++;

    // the build: a drop announced within 8 bars
    const ev = director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const build = beatsToDrop != null && beatsToDrop < 32 ? clamp01((32 - beatsToDrop) / 28) : (director.mode === 'build' ? 0.4 : 0);
    this._build += (build - this._build) * Math.min(1, dt / 0.4);
    // the drop: the announced mark crossed, or adapt's event
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    let drop = a.drop === true;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; drop = true; }
    }

    // the VJ's decisions
    if (drop && this._time - this._dropAt > 4 * this._beatPeriod) {
      this._dropAt = this._time; this._build = 0; this._dropSet();
      if (this._comfort.requestFlash(this._time)) this._flash = VDJ_DROP_FLASH;
    } else if (phraseEdge) {
      this._newSet('phrase'); this._pendingSet = false;
    } else if (barEdge && this._pendingSet) {
      this._newSet('scene'); this._pendingSet = false;      // the scene's cut lands on a downbeat, never mid-bar
    } else if (barEdge) {
      this._barChange();
    }
    if (this._clock.wrapped) this._pendingSet = true;

    // the uniforms
    const B = this._bus, M = this._mixU, s = this._state;
    const since = this._time - this._lastBeatAt;
    B.uTime.value = this._time; B.uBeats.value = this._beats; B.uBeatPhase.value = bp;
    B.uBarPhase.value = barPhase != null ? barPhase : (this._beats % 4) / 4; B.uBarIndex.value = this._barCount;
    B.uPulse.value = Math.exp(-since / (this._beatPeriod * 0.3));
    B.uBarPulse.value = Math.exp(-(this._time - this._lastBarAt) / (this._beatPeriod * 0.6));
    B.uEnergy.value = this._energy;
    this._frontFlash = Math.max(0, this._frontFlash - dt / 0.06);
    this._zoomKick *= Math.exp(-dt / (this._beatPeriod * 0.35));
    M.uFrontGain.value = 1 + 1.2 * this._frontFlash;
    M.uZoom.value = 1.012 + 0.05 * this._zoomKick + 0.02 * this._energy;
    this._sinceRender += dt;                                             // the trail decays per RENDER (see _render): a probe steps many updates per frame
    M.uDither.value = 0.5;
    M.uInvert.value = this._time < this._invertUntil ? 1 : 0;
    M.uRings.value.set(this._rings[0], this._rings[1], this._rings[2], this._rings[3]);
    // the build empties the layers; the strobe speeds up
    const bld = this._build;
    const midOn = this._layout.mid && bld < 0.35;
    const bgOn = bld < 0.7;
    M.uHasMid.value = midOn ? 1 : 0; M.uBgOn.value = bgOn ? 1 : 0;
    this._frontNow = bld > 0.5 ? 'strobe' : s.front;
    M.uFront.value = FRONT.indexOf(this._frontNow);
    M.uStrobeRate.value = bld > 0.75 ? 4 : bld > 0.5 ? 2 : 1;           // 1, 2, 4 flashes per beat: integer subdivisions stay phase-locked to the beat (a continuous rate drifted and missed the downbeats); 4 at 128 BPM is 8.5 Hz, the cap
    M.uBlend1.value = BLEND1.indexOf(s.blend1); M.uBlend2.value = { add: 0, screen: 1, diff: 3, mask: 4 }[s.blend2];
    M.uMirror.value = s.mirror;
    M.uPoster.value = s.poster ? 1 : 0;
    M.uSplit.value = 1.5 * B.uPulse.value;
    M.uFlash.value = this._flash; this._flash = 0;
    this._fbOn = !!(this._feedback && (this._forcedFb != null ? this._forcedFb : s.feedback));
    this._post.update(vdjBeatChroma(bp, this._beatPeriod));
    // comfort
    this._lumAge += dt;
    if (this._lumAge > 0.5) { this._lumAge = 0; this._lumPending = true; }
    if (this._lumFresh) { this._lumFresh = false; M.uGain.value = this._comfort.measure(this._lumMeasured, this._lumSince || 0.5); this._lumSince = 0; }
    this._lumSince = (this._lumSince || 0) + dt;
  }

  _pass(renderer, material, target) {
    this._passQuad.material = material;
    renderer.setRenderTarget(target);
    renderer.render(this._passScene, this._ortho);
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const prev = renderer.getRenderTarget();
    let W = this._size.x, H = this._size.y;
    if (prev) { W = prev.width; H = prev.height; }
    W = Math.max(2, W); H = Math.max(2, H);
    const B = this._bus, M = this._mixU, s = this._state;
    B.uAspect.value = W / H;
    if (!this._compiled) {
      // every generator once into a tiny target: nothing compiles on a bar later
      this._compiled = true;
      B.uRes.value.set(4, 4);
      for (const k of BG) this._pass(renderer, this._bgMats[k], this._rtTmp);
      for (const k of MID) if (this._layout.mid) this._pass(renderer, this._midMats[k], this._rtTmp);
      for (const mode of [0, 1, 2]) { M.uMode.value = mode; this._pass(renderer, this._mixMat, this._rtTmp); }
    }
    // the background
    const bw = Math.max(2, Math.round(W * this._layout.bgScale)), bh = Math.max(2, Math.round(H * this._layout.bgScale));
    if (this._rtBg.width !== bw || this._rtBg.height !== bh) this._rtBg.setSize(bw, bh);
    if (M.uBgOn.value > 0.5) { B.uRes.value.set(bw, bh); this._pass(renderer, this._bgMats[s.bg], this._rtBg); }
    M.uBg.value = this._rtBg.texture;
    // the middle
    if (this._rtMid && M.uHasMid.value > 0.5) {
      if (this._rtMid.width !== W || this._rtMid.height !== H) this._rtMid.setSize(W, H);
      B.uRes.value.set(W, H);
      this._pass(renderer, this._midMats[s.mid], this._rtMid);
      M.uMid.value = this._rtMid.texture;
    }
    // the mix. Half-life a sixteenth of a bar (R15), 0.91 per frame at 60 Hz: a beat (0.976) still kept each pixel's
    // brightest value of the last 30 frames under the max, a uniform wash. Measured over the time since the last render.
    M.uDecay.value = Math.pow(0.5, Math.min(this._sinceRender, 1) / (this._beatPeriod * 0.25));
    this._sinceRender = 0;
    if (this._fbOn) {
      const fb = this._feedback;
      fb.ensureSize(renderer);
      B.uRes.value.set(fb.width, fb.height);
      M.uPrev.value = fb.read.texture; M.uMode.value = 0;
      this._pass(renderer, this._mixMat, fb.write);
      M.uPrev.value = fb.write.texture; M.uMode.value = 1;
      fb.swap();
    } else {
      if (this._feedback && this._feedback.width && this._fbWasOn) this._feedback.clear(renderer);    // off: the trail is gone when it comes back
      M.uMode.value = 2;
    }
    this._fbWasOn = this._fbOn;
    B.uRes.value.set(W, H);
    M.uMeasureK.value = 1;
    // comfort: the display pass into a small target, the pixel-width front scaled to its true coverage
    if (this._lumPending) {
      this._lumPending = false;
      B.uRes.value.set(LUM_W, LUM_H); M.uMeasureK.value = LUM_H / H;
      this._pass(renderer, this._mixMat, this._lumTarget);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_W, LUM_H, this._lumPixels);
      B.uRes.value.set(W, H); M.uMeasureK.value = 1;
      let sum = 0; const px = this._lumPixels;
      for (let i = 0; i < px.length; i += 4) sum += (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) / 255;
      this._lumMeasured = sum / (LUM_W * LUM_H);
      this._lumFresh = true;
    }
    renderer.setRenderTarget(prev);
  }

  get probe() {
    const a = this._adapt.get();
    return {
      tier: this._tier, state: Object.assign({}, this._state), frontNow: this._frontNow, feedbackOn: this._fbOn, build: +this._build.toFixed(2), intensity: +a.intensity.toFixed(2),
      bars: this._barCount, phrases: this._phraseCount, beats: +this._beats.toFixed(2), memory: this._memory.slice(), decisions: this._decisions.slice(-12),
      gain: +this._mixU.uGain.value.toFixed(2), lum: +this._lumMeasured.toFixed(3), invert: this._mixU.uInvert.value, hasCover: this._bus.uHasCover.value, title: this._titleKey || null,
      scene: +this._clock.t.toFixed(2)
    };
  }

  dispose() {
    if (this._display) { this._display.onBeforeRender = null; if (this._scene) this._scene.remove(this._display); this._display.geometry.dispose(); }
    if (this._passQuad) this._passQuad.geometry.dispose();
    for (const m of Object.values(this._bgMats || {})) m.dispose();
    for (const m of Object.values(this._midMats || {})) m.dispose();
    if (this._mixMat) this._mixMat.dispose();
    for (const t of [this._rtBg, this._rtMid, this._rtTmp, this._lumTarget]) if (t) t.dispose();
    if (this._feedback) this._feedback.dispose();
    this._dropCover();
    if (this._title) { this._title.texture.dispose(); this._title = null; }
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}
